import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../api/axios';
import { C } from '../theme';

const TABS = [
  { key: 'stock',  label: 'Stock',          icon: '📦' },
  { key: 'credit', label: 'Credit History', icon: '💳' },
  { key: 'usage',  label: 'Credit Usage',   icon: '📊' },
];

const PAYMENT_MODES = ['Cash', 'UPI', 'NEFT', 'RTGS', 'Cheque', 'Bank Transfer', 'Other'];
const BLANK_CREDIT  = { amount: '', description: '', payment_mode: 'Cash', reference: '' };

export default function DealerDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();

  // dealer
  const [dealer,  setDealer]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState('stock');
  const [toast,   setToast]   = useState(null);

  // credit
  const [balance,      setBalance]      = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [credLoading,  setCredLoading]  = useState(false);
  const [credModal,    setCredModal]    = useState(false);
  const [credForm,     setCredForm]     = useState(BLANK_CREDIT);
  const [credSaving,   setCredSaving]   = useState(false);
  const [credErr,      setCredErr]      = useState('');

  // stock
  const [stockOrders,  setStockOrders]  = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [stockModal,   setStockModal]   = useState(false);
  const [stockSaving,  setStockSaving]  = useState(false);
  const [stockErr,     setStockErr]     = useState('');
  const [expanded,     setExpanded]     = useState(null);

  // stock wizard
  const [stockStep,   setStockStep]   = useState(1);          // 1 | 2 | 3
  const [selCats,     setSelCats]     = useState([]);          // [{ id, name, product_name }]
  const [selTypes,    setSelTypes]    = useState({});          // { [catId]: typeObj }
  const [costPerPair, setCostPerPair] = useState('');
  const [numPairs,    setNumPairs]    = useState(1);
  const [categories,  setCategories]  = useState([]);
  const [typesMap,    setTypesMap]    = useState({});          // { [catId]: type[] }

  // serial assignment
  const [serialModal,   setSerialModal]   = useState(false);
  const [serialOrder,   setSerialOrder]   = useState(null);
  const [serialLines,   setSerialLines]   = useState([]);     // [{ stock_item_id, category_name, type_name, num_pairs, assigned, available }]
  const [pairSel,       setPairSel]       = useState([]);     // [{ [stock_item_id]: inv_id|null }, ...] length=num_pairs
  const [serialLoading, setSerialLoading] = useState(false);
  const [serialSaving,  setSerialSaving]  = useState(false);
  const [serialErr,     setSerialErr]     = useState('');

  const typesLoading = useRef({});

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  // ── loaders ──────────────────────────────────────────────────────────────────

  const loadDealer = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/dealers/${id}`);
      setDealer(r.data);
    } catch { navigate('/dealers'); }
    finally { setLoading(false); }
  }, [id, navigate]);

  const loadCredits = useCallback(async () => {
    setCredLoading(true);
    try {
      const r = await api.get(`/api/dealers/${id}/credits`);
      setBalance(r.data.balance);
      setTransactions(r.data.transactions);
    } catch { showToast('error', 'Failed to load credit history.'); }
    finally { setCredLoading(false); }
  }, [id]);

  const loadStock = useCallback(async () => {
    setStockLoading(true);
    try {
      const r = await api.get(`/api/dealers/${id}/stock`);
      setStockOrders(r.data);
    } catch { showToast('error', 'Failed to load stock orders.'); }
    finally { setStockLoading(false); }
  }, [id]);

  useEffect(() => { loadDealer(); loadCredits(); }, [loadDealer, loadCredits]);
  useEffect(() => {
    if (tab === 'stock')  loadStock();
    if (tab === 'credit') loadCredits();
  }, [tab, loadCredits, loadStock]);

  // ── categories / types ───────────────────────────────────────────────────────

  const ensureCategories = async () => {
    if (categories.length > 0) return;
    try {
      const r = await api.get('/api/categories');
      setCategories(r.data);
    } catch { showToast('error', 'Failed to load categories.'); }
  };

  const ensureTypes = async (catId) => {
    if (!catId || typesMap[catId] !== undefined || typesLoading.current[catId]) return;
    typesLoading.current[catId] = true;
    try {
      const r = await api.get(`/api/categories/${catId}/types`);
      setTypesMap(prev => ({ ...prev, [catId]: r.data }));
    } catch { setTypesMap(prev => ({ ...prev, [catId]: [] })); }
    finally { typesLoading.current[catId] = false; }
  };

  // ── wizard helpers ───────────────────────────────────────────────────────────

  const openStockModal = async () => {
    await ensureCategories();
    setStockStep(1);
    setSelCats([]);
    setSelTypes({});
    setCostPerPair('');
    setNumPairs(1);
    setStockErr('');
    setStockModal(true);
  };

  const toggleCat = (cat) => {
    setSelCats(prev => {
      const exists = prev.find(c => c.id === cat.id);
      if (exists) return prev.filter(c => c.id !== cat.id);
      if (prev.length >= 3) return prev;  // max 3
      return [...prev, cat];
    });
    setStockErr('');
  };

  const goToStep2 = async () => {
    if (selCats.length === 0) { setStockErr('Select at least one category.'); return; }
    setStockErr('');
    // Pre-fetch types for all selected cats
    await Promise.all(selCats.map(c => ensureTypes(c.id)));
    setStockStep(2);
  };

  const selectType = (catId, typeObj) => {
    setSelTypes(prev => ({ ...prev, [catId]: typeObj }));
    setStockErr('');
  };

  // The dealer price of a type: `dealer_amount` when defined, else the standard amount.
  const dealerUnitPrice = (t) =>
    t ? parseFloat(t.dealer_amount != null ? t.dealer_amount : (t.amount || 0)) || 0 : 0;

  // True when a selected type has no dealer_amount set and we're falling back
  const usesFallbackPrice = selCats.some(cat => {
    const t = selTypes[cat.id];
    return t && t.dealer_amount == null;
  });

  const goToStep3 = () => {
    for (const cat of selCats) {
      if (!selTypes[cat.id]) { setStockErr(`Select a type for "${cat.name}".`); return; }
    }
    // Cost per pair is derived from each type's DEALER amount — not editable
    const autoAmt = selCats.reduce((sum, cat) => sum + dealerUnitPrice(selTypes[cat.id]), 0);
    setCostPerPair(autoAmt > 0 ? autoAmt.toFixed(2) : '0');
    setNumPairs(1);
    setStockErr('');
    setStockStep(3);
  };

  const orderTotal = (parseFloat(costPerPair) || 0) * (parseInt(numPairs) || 0);

  const saveStock = async () => {
    const np = parseInt(numPairs);
    if (!np || np < 1) { setStockErr('Number of pairs must be at least 1.'); return; }
    if (orderTotal <= 0) {
      setStockErr('Dealer price for the selected type(s) is ₹0. Set a Dealer Amount on the category type first.');
      return;
    }
    if (orderTotal > balance) {
      setStockErr(`Insufficient balance. Available: ₹${fmtNum(balance)}, Required: ₹${fmtNum(orderTotal)}`);
      return;
    }
    setStockSaving(true); setStockErr('');
    try {
      // No cost_per_pair sent — the server prices from the category type's dealer_amount
      await api.post(`/api/dealers/${id}/stock`, {
        items: selCats.map(cat => ({
          category_id: cat.id,
          type_id:     selTypes[cat.id]?.id ?? null,
        })),
        num_pairs: np,
      });
      await loadStock();
      await loadCredits();
      showToast('success', `Stock order created — ₹${fmtNum(orderTotal)} debited.`);
      setStockModal(false);
    } catch (ex) {
      setStockErr(ex?.response?.data?.detail || 'Failed to create stock order.');
    } finally { setStockSaving(false); }
  };

  // ── serial assignment helpers ─────────────────────────────────────────────────

  const openSerialModal = async (order) => {
    setSerialOrder(order);
    setSerialErr('');
    setSerialLoading(true);
    setSerialModal(true);
    try {
      const r = await api.get(`/api/dealers/${id}/stock/${order.id}/serials`);
      setSerialLines(r.data);
      const np = order.num_pairs;
      // init pairSel: each pair gets the already-assigned item at that index (if any)
      const init = Array.from({ length: np }, (_, pairIdx) => {
        const slot = {};
        r.data.forEach(line => {
          slot[line.stock_item_id] = line.assigned[pairIdx]?.id ?? null;
        });
        return slot;
      });
      setPairSel(init);
    } catch { setSerialErr('Failed to load inventory items.'); }
    finally { setSerialLoading(false); }
  };

  const selectForPair = (pairIdx, stockItemId, invId) => {
    setPairSel(prev => {
      const next = prev.map((p, i) => i === pairIdx ? { ...p, [stockItemId]: invId } : p);
      return next;
    });
    setSerialErr('');
  };

  const usedInOtherPair = (pairIdx, stockItemId, invId) =>
    pairSel.some((p, i) => i !== pairIdx && p[stockItemId] === invId);

  const saveSerials = async () => {
    for (let pi = 0; pi < pairSel.length; pi++) {
      for (const line of serialLines) {
        if (!pairSel[pi][line.stock_item_id]) {
          setSerialErr(`Pair ${pi + 1}: select a ${line.category_name} (${line.type_name}).`);
          return;
        }
      }
    }
    setSerialSaving(true); setSerialErr('');
    try {
      const assignments = {};
      serialLines.forEach(line => {
        assignments[String(line.stock_item_id)] = pairSel.map(p => p[line.stock_item_id]).filter(Boolean);
      });
      await api.post(`/api/dealers/${id}/stock/${serialOrder.id}/serials`, { assignments });
      await loadStock();
      showToast('success', 'Serial numbers assigned successfully.');
      setSerialModal(false);
    } catch (ex) {
      setSerialErr(ex?.response?.data?.detail || 'Failed to assign serials.');
    } finally { setSerialSaving(false); }
  };

  // ── credit helpers ────────────────────────────────────────────────────────────

  const saveCredit = async () => {
    const amt = parseFloat(credForm.amount);
    if (!credForm.amount || isNaN(amt) || amt <= 0) { setCredErr('Enter a valid amount.'); return; }
    setCredSaving(true); setCredErr('');
    try {
      await api.post(`/api/dealers/${id}/credits`, {
        amount:       amt,
        description:  credForm.description.trim() || null,
        payment_mode: credForm.payment_mode || null,
        reference:    credForm.reference.trim() || null,
      });
      await loadCredits();
      showToast('success', `₹${fmtNum(amt)} credit added.`);
      setCredModal(false); setCredForm(BLANK_CREDIT);
    } catch (ex) {
      setCredErr(ex?.response?.data?.detail || 'Failed to add credit.');
    } finally { setCredSaving(false); }
  };

  const toggleStatus = async () => {
    const next = dealer.status === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/api/dealers/${id}`, { status: next });
      setDealer(prev => ({ ...prev, status: next }));
    } catch { showToast('error', 'Status update failed.'); }
  };

  // ── helpers ───────────────────────────────────────────────────────────────────

  const fmtNum  = (n) => Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 });
  const fmtAmt  = (n) => '₹' + fmtNum(n);
  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
      + ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  if (loading) return (
    <Layout title="Dealer Details">
      <div style={{ textAlign: 'center', padding: '80px', color: C.textSub }}>Loading…</div>
    </Layout>
  );
  if (!dealer) return null;

  const isActive = dealer.status === 'active';

  return (
    <Layout title="Dealer Details">

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '24px', zIndex: 4000,
          background: toast.type === 'success' ? C.successBg : C.dangerBg,
          color:      toast.type === 'success' ? C.successText : C.dangerText,
          border:     `1px solid ${toast.type === 'success' ? '#6EE7B7' : '#FCA5A5'}`,
          borderRadius: '10px', padding: '12px 20px', fontSize: '13px', fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          {toast.type === 'success' ? '✓' : '⚠'} {toast.text}
        </div>
      )}

      {/* Back */}
      <button onClick={() => navigate('/dealers')} style={{
        background: 'none', border: 'none', cursor: 'pointer', color: C.textSub,
        fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center',
        gap: '6px', marginBottom: '20px', padding: 0,
      }}>
        ← Back to Dealers
      </button>

      {/* Info card */}
      <div style={{
        background: C.white, borderRadius: '14px',
        border: `1.5px solid ${isActive ? '#7C3AED' : C.border}`,
        boxShadow: isActive ? '0 0 0 3px #EDE9FE, 0 2px 8px rgba(0,0,0,0.06)' : '0 1px 4px rgba(0,0,0,0.05)',
        overflow: 'hidden', marginBottom: '20px',
      }}>
        <div style={{
          background: isActive ? 'linear-gradient(135deg, #3B0764 0%, #1E1040 100%)' : '#F9FAFB',
          padding: '22px 24px', display: 'flex', alignItems: 'flex-start', gap: '16px',
        }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '14px', flexShrink: 0,
            background: 'linear-gradient(135deg, #7C3AED, #5B21B6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: '24px', color: '#fff', boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
          }}>
            {(dealer.company_name || '?')[0].toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '18px', fontWeight: 800, color: isActive ? '#fff' : C.textMain }}>{dealer.company_name}</div>
            <div style={{ fontSize: '12px', fontFamily: 'monospace', fontWeight: 600, marginTop: '3px', color: isActive ? 'rgba(255,255,255,0.45)' : '#6D28D9' }}>{dealer.dealer_id}</div>
            <div style={{ marginTop: '10px', display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
              <span style={{
                background: isActive ? 'rgba(52,211,153,0.18)' : 'rgba(248,113,113,0.18)',
                color:      isActive ? '#6EE7B7' : '#FCA5A5',
                border:     `1px solid ${isActive ? 'rgba(52,211,153,0.35)' : 'rgba(248,113,113,0.35)'}`,
                borderRadius: '20px', padding: '3px 12px', fontSize: '11px', fontWeight: 700,
              }}>{isActive ? '● Active' : '○ Inactive'}</span>
              {dealer.panel_access && (
                <span style={{
                  background: 'rgba(167,139,250,0.18)', color: isActive ? '#C4B5FD' : '#6D28D9',
                  border: '1px solid rgba(167,139,250,0.35)',
                  borderRadius: '20px', padding: '3px 12px', fontSize: '11px', fontWeight: 700,
                }}>🔐 {dealer.username}</span>
              )}
            </div>
          </div>
          <button onClick={toggleStatus} style={{
            padding: '8px 16px', background: 'rgba(255,255,255,0.10)',
            color: isActive ? '#fff' : C.textMain,
            border: `1px solid ${isActive ? 'rgba(255,255,255,0.22)' : C.border}`,
            borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', flexShrink: 0,
          }}>{isActive ? 'Deactivate' : 'Activate'}</button>
        </div>
        <div style={{ padding: '14px 24px', display: 'flex', gap: '28px', flexWrap: 'wrap', borderTop: isActive ? '1px solid rgba(124,58,237,0.12)' : `1px solid ${C.border}` }}>
          {[
            { icon: '👤', label: 'Contact', val: dealer.contact_person },
            { icon: '📞', label: 'Phone',   val: dealer.contact_number },
            { icon: '✉',  label: 'Email',   val: dealer.email          },
            { icon: '🏛', label: 'GST',     val: dealer.gst_number     },
          ].filter(x => x.val).map(({ icon, label, val }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '13px' }}>{icon}</span>
              <span style={{ fontSize: '11px', fontWeight: 600, color: C.textSub }}>{label}:</span>
              <span style={{ fontSize: '13px', fontWeight: 600, color: C.textMain }}>{val}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs card */}
      <div style={{ background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>

        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, padding: '0 22px' }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: '14px 18px', border: 'none', cursor: 'pointer', background: 'none',
              fontSize: '13px', fontWeight: tab === t.key ? 700 : 500,
              color: tab === t.key ? '#7C3AED' : C.textSub,
              borderBottom: tab === t.key ? '2.5px solid #7C3AED' : '2.5px solid transparent',
              display: 'flex', alignItems: 'center', gap: '6px',
              transition: 'color 0.15s', marginBottom: '-1px', whiteSpace: 'nowrap',
            }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* ── STOCK TAB ── */}
        {tab === 'stock' && (
          <div style={{ padding: '22px 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <StatChip label="Orders"        val={stockOrders.length} color="#6D28D9" bg="#F5F3FF" />
                <StatChip label="Total Spent"   val={fmtAmt(stockOrders.reduce((s, o) => s + o.total_amount, 0))} color={C.danger} bg={C.dangerBg} />
                <StatChip label="Wallet Balance" val={fmtAmt(balance)} color={balance >= 0 ? '#059669' : C.danger} bg={balance >= 0 ? C.successBg : C.dangerBg} />
              </div>
              <button onClick={openStockModal} style={btnPurple}>
                📦 Add Stock
              </button>
            </div>

            {stockLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: C.textSub }}>Loading…</div>
            ) : stockOrders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '52px', background: C.primaryBg, borderRadius: '12px', border: `2px dashed ${C.border}` }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>📦</div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: C.textMain, marginBottom: '6px' }}>No Stock Orders Yet</div>
                <div style={{ fontSize: '13px', color: C.textSub }}>Dealer must have wallet credit before adding stock.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {stockOrders.map(order => (
                  <div key={order.id} style={{ border: `1px solid ${C.border}`, borderRadius: '12px', overflow: 'hidden' }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '14px',
                      padding: '14px 18px',
                      background: expanded === order.id ? '#F5F3FF' : C.white,
                      transition: 'background 0.15s',
                    }}>
                      <div
                        onClick={() => setExpanded(expanded === order.id ? null : order.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: 0, cursor: 'pointer' }}
                      >
                        <div style={{
                          width: '38px', height: '38px', borderRadius: '10px', flexShrink: 0,
                          background: 'linear-gradient(135deg, #7C3AED, #5B21B6)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px',
                        }}>📦</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: '13px', color: C.textMain }}>{order.order_id}</div>
                          <div style={{ fontSize: '11px', color: C.textSub, marginTop: '2px' }}>
                            {fmtDate(order.created_at)} · {order.items.length} item{order.items.length !== 1 ? 's' : ''}
                            {order.num_pairs ? ` · ${order.num_pairs} pair${order.num_pairs !== 1 ? 's' : ''}` : ''}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontWeight: 800, fontSize: '15px', color: '#4C1D95' }}>{fmtAmt(order.total_amount)}</div>
                          {order.cost_per_pair && (
                            <div style={{ fontSize: '10px', color: C.textSub, marginTop: '2px' }}>
                              {fmtAmt(order.cost_per_pair)} / pair
                            </div>
                          )}
                        </div>
                        <span style={{ color: '#7C3AED', fontSize: '12px', marginLeft: '4px' }}>
                          {expanded === order.id ? '▲' : '▼'}
                        </span>
                      </div>

                      {/* Serial assignment button */}
                      <button
                        onClick={e => { e.stopPropagation(); openSerialModal(order); }}
                        style={{
                          flexShrink: 0, padding: '6px 12px',
                          background: order.serials_assigned >= order.serials_needed && order.serials_needed > 0
                            ? C.successBg : '#F5F3FF',
                          color: order.serials_assigned >= order.serials_needed && order.serials_needed > 0
                            ? '#059669' : '#6D28D9',
                          border: `1px solid ${order.serials_assigned >= order.serials_needed && order.serials_needed > 0 ? '#6EE7B7' : '#DDD6FE'}`,
                          borderRadius: '8px', fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap',
                        }}
                      >
                        🔢 {order.serials_assigned}/{order.serials_needed} Serials
                      </button>
                    </div>

                    {expanded === order.id && (
                      <div style={{ borderTop: `1px solid ${C.border}`, background: '#FAFAFA' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ background: C.primaryBg }}>
                              {['Category', 'Type', 'Pairs'].map(h => (
                                <th key={h} style={th}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {order.items.map(it => (
                              <tr key={it.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                                <td style={td}><span style={{ fontWeight: 600 }}>{it.category_name || '—'}</span></td>
                                <td style={td}>{it.type_name || <span style={{ color: C.textSub }}>—</span>}</td>
                                <td style={td}>{it.quantity}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {order.notes && (
                          <div style={{ padding: '10px 18px', fontSize: '12px', color: C.textSub, borderTop: `1px solid ${C.border}` }}>
                            📝 {order.notes}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── CREDIT HISTORY TAB ── */}
        {tab === 'credit' && (() => {
          const credits = transactions.filter(t => t.type === 'credit');
          return (
          <div style={{ padding: '22px 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '22px', flexWrap: 'wrap', gap: '14px' }}>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{
                  background: 'linear-gradient(135deg,#ECFDF5,#D1FAE5)',
                  border: '1.5px solid #6EE7B7', borderRadius: '12px', padding: '14px 22px',
                }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: '#059669', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '5px' }}>💰 Total Credited</div>
                  <div style={{ fontSize: '24px', fontWeight: 800, color: '#065F46' }}>{fmtAmt(credits.reduce((s,t)=>s+t.amount,0))}</div>
                </div>
                <StatChip label="Wallet Balance" val={fmtAmt(balance)} color={balance >= 0 ? '#6D28D9' : C.danger} bg={balance >= 0 ? '#F5F3FF' : C.dangerBg} />
                <StatChip label="Transactions"   val={credits.length} color={C.textSub} bg={C.primaryBg} />
              </div>
              <button onClick={() => { setCredForm(BLANK_CREDIT); setCredErr(''); setCredModal(true); }} style={btnPurple}>
                + Add Credit
              </button>
            </div>

            {credLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: C.textSub }}>Loading…</div>
            ) : credits.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '52px', background: C.primaryBg, borderRadius: '12px', border: `2px dashed ${C.border}` }}>
                <div style={{ fontSize: '36px', marginBottom: '10px' }}>💳</div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: C.textMain, marginBottom: '6px' }}>No Credits Yet</div>
                <div style={{ fontSize: '13px', color: C.textSub }}>Add a credit to top up this dealer's wallet.</div>
              </div>
            ) : (
              <TransactionTable rows={credits} fmtDate={fmtDate} fmtAmt={fmtAmt} />
            )}
          </div>
          );
        })()}

        {/* ── CREDIT USAGE TAB ── */}
        {tab === 'usage' && (() => {
          const debits = transactions.filter(t => t.type === 'debit');
          return (
          <div style={{ padding: '22px 24px' }}>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '22px' }}>
              <div style={{
                background: 'linear-gradient(135deg,#FEF2F2,#FEE2E2)',
                border: '1.5px solid #FECACA', borderRadius: '12px', padding: '14px 22px',
              }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: C.danger, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '5px' }}>📉 Total Used</div>
                <div style={{ fontSize: '24px', fontWeight: 800, color: '#7F1D1D' }}>{fmtAmt(debits.reduce((s,t)=>s+t.amount,0))}</div>
              </div>
              <StatChip label="Wallet Balance" val={fmtAmt(balance)} color={balance >= 0 ? '#6D28D9' : C.danger} bg={balance >= 0 ? '#F5F3FF' : C.dangerBg} />
              <StatChip label="Usages" val={debits.length} color={C.textSub} bg={C.primaryBg} />
            </div>

            {credLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: C.textSub }}>Loading…</div>
            ) : debits.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '52px', background: C.primaryBg, borderRadius: '12px', border: `2px dashed ${C.border}` }}>
                <div style={{ fontSize: '36px', marginBottom: '10px' }}>📊</div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: C.textMain, marginBottom: '6px' }}>No Credit Usage Yet</div>
                <div style={{ fontSize: '13px', color: C.textSub }}>Debits appear here when stock is assigned.</div>
              </div>
            ) : (
              <TransactionTable rows={debits} fmtDate={fmtDate} fmtAmt={fmtAmt} />
            )}
          </div>
          );
        })()}

      </div>

      {/* ── ADD STOCK WIZARD MODAL ── */}
      {stockModal && (
        <Overlay>
          <div style={{
            background: C.white, borderRadius: '16px', width: '100%', maxWidth: '620px',
            maxHeight: '90vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 24px 80px rgba(0,0,0,0.3)', border: `1px solid ${C.border}`,
          }}>
            {/* Wizard header */}
            <div style={{
              background: 'linear-gradient(135deg, #3B0764, #1E1040)',
              padding: '20px 24px', flexShrink: 0,
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#fff' }}>
                  {stockStep === 1 && '📂 Step 1 — Select Categories'}
                  {stockStep === 2 && '🔖 Step 2 — Select Type'}
                  {stockStep === 3 && '✅ Step 3 — Confirm Order'}
                </div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', marginTop: '3px' }}>
                  Wallet: <strong style={{ color: balance >= 0 ? '#A78BFA' : '#FCA5A5' }}>₹{fmtNum(balance)}</strong>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {/* Step dots */}
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  {[1, 2, 3].map(s => (
                    <div key={s} style={{
                      width: s === stockStep ? '22px' : '8px',
                      height: '8px', borderRadius: '4px',
                      background: s === stockStep ? '#A78BFA' : s < stockStep ? '#6D28D9' : 'rgba(255,255,255,0.2)',
                      transition: 'all 0.25s',
                    }} />
                  ))}
                </div>
                <button onClick={() => setStockModal(false)} style={{
                  background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '50%',
                  width: '28px', height: '28px', cursor: 'pointer',
                  color: 'rgba(255,255,255,0.7)', fontSize: '14px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>✕</button>
              </div>
            </div>

            {/* Wizard body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '22px 24px' }}>

              {/* ── STEP 1: Category cards ── */}
              {stockStep === 1 && (
                <div>
                  <div style={{ fontSize: '12px', color: C.textSub, marginBottom: '16px' }}>
                    Select up to 3 categories. <strong style={{ color: '#6D28D9' }}>{selCats.length} selected</strong>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
                    {categories.map(cat => {
                      const isSel = selCats.some(c => c.id === cat.id);
                      return (
                        <div
                          key={cat.id}
                          onClick={() => toggleCat(cat)}
                          style={{
                            padding: '16px 14px', borderRadius: '12px', cursor: 'pointer',
                            border: `2px solid ${isSel ? '#7C3AED' : C.border}`,
                            background: isSel ? '#F5F3FF' : C.white,
                            boxShadow: isSel ? '0 0 0 3px #EDE9FE' : 'none',
                            transition: 'all 0.15s', textAlign: 'center',
                            opacity: (!isSel && selCats.length >= 3) ? 0.4 : 1,
                          }}
                        >
                          <div style={{
                            width: '44px', height: '44px', borderRadius: '12px', margin: '0 auto 10px',
                            background: isSel ? 'linear-gradient(135deg,#7C3AED,#5B21B6)' : 'linear-gradient(135deg,#EDE9FE,#DDD6FE)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px',
                          }}>📂</div>
                          <div style={{ fontWeight: 700, fontSize: '13px', color: isSel ? '#4C1D95' : C.textMain }}>{cat.name}</div>
                          {cat.product_name && (
                            <div style={{ fontSize: '11px', color: C.textSub, marginTop: '3px' }}>{cat.product_name}</div>
                          )}
                          {isSel && (
                            <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'center' }}>
                              <span style={{ background: '#7C3AED', color: '#fff', borderRadius: '10px', fontSize: '10px', fontWeight: 700, padding: '2px 10px' }}>✓ Selected</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {categories.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px', color: C.textSub }}>Loading categories…</div>
                  )}
                </div>
              )}

              {/* ── STEP 2: Type selection per category ── */}
              {stockStep === 2 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                  {selCats.map(cat => {
                    const types    = typesMap[cat.id] || [];
                    const selected = selTypes[cat.id];
                    return (
                      <div key={cat.id}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                          <span style={{ fontSize: '18px' }}>📂</span>
                          <span style={{ fontWeight: 700, fontSize: '14px', color: C.textMain }}>{cat.name}</span>
                          {cat.product_name && <span style={{ fontSize: '12px', color: C.textSub }}>· {cat.product_name}</span>}
                        </div>
                        {types.length === 0 ? (
                          <div style={{ padding: '14px', background: C.primaryBg, borderRadius: '10px', fontSize: '12px', color: C.textSub, textAlign: 'center' }}>
                            No types available for this category
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                            {types.map(t => {
                              const isSel = selected?.id === t.id;
                              return (
                                <div
                                  key={t.id}
                                  onClick={() => selectType(cat.id, t)}
                                  style={{
                                    padding: '10px 16px', borderRadius: '10px', cursor: 'pointer',
                                    border: `2px solid ${isSel ? '#7C3AED' : C.border}`,
                                    background: isSel ? '#F5F3FF' : C.white,
                                    boxShadow: isSel ? '0 0 0 3px #EDE9FE' : 'none',
                                    transition: 'all 0.15s',
                                  }}
                                >
                                  <div style={{ fontWeight: 700, fontSize: '13px', color: isSel ? '#4C1D95' : C.textMain }}>{t.name}</div>
                                  {t.amount > 0 && (
                                    <div style={{ fontSize: '11px', color: isSel ? '#6D28D9' : C.textSub, marginTop: '2px' }}>
                                      ₹{fmtNum(t.amount)}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ── STEP 3: Cost + pairs ── */}
              {stockStep === 3 && (
                <div>
                  {/* Selected summary */}
                  <div style={{ marginBottom: '22px', padding: '14px 16px', background: '#F5F3FF', borderRadius: '12px', border: '1px solid #DDD6FE' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#6D28D9', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>Order Summary</div>
                    {selCats.map(cat => {
                      const t = selTypes[cat.id];
                      return (
                        <div key={cat.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                          <span style={{ fontSize: '13px', fontWeight: 600, color: C.textMain }}>
                            📂 {cat.name} → {t?.name || '—'}
                          </span>
                          {t?.amount > 0 && (
                            <span style={{ fontSize: '12px', color: '#6D28D9', fontWeight: 600 }}>₹{fmtNum(t.amount)}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Cost per pair — derived from each type's Dealer Amount, not editable */}
                  <div style={{ marginBottom: '18px' }}>
                    <label style={lbl}>Cost per pair (₹) — from Dealer Amount</label>
                    <div style={{ border: '1.5px solid #DDD6FE', borderRadius: '10px', overflow: 'hidden' }}>
                      {selCats.map(cat => {
                        const t    = selTypes[cat.id];
                        const unit = dealerUnitPrice(t);
                        const fell = t && t.dealer_amount == null;
                        return (
                          <div key={cat.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', padding: '9px 14px', borderBottom: '1px solid #EDE9FE', background: '#FAFAFF' }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: '12px', fontWeight: 600, color: C.textMain }}>{cat.name} → {t?.name || '—'}</div>
                              {fell && (
                                <div style={{ fontSize: '10px', color: '#B45309', fontWeight: 600 }}>
                                  ⚠ No Dealer Amount set — using standard ₹{fmtNum(t?.amount || 0)}
                                </div>
                              )}
                            </div>
                            <span style={{ fontSize: '14px', fontWeight: 800, color: unit > 0 ? '#6D28D9' : C.danger, whiteSpace: 'nowrap' }}>
                              ₹{fmtNum(unit)}
                            </span>
                          </div>
                        );
                      })}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 14px', background: '#F5F3FF' }}>
                        <span style={{ fontSize: '12px', fontWeight: 800, color: '#4C1D95', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Per pair</span>
                        <span style={{ fontSize: '20px', fontWeight: 900, color: '#4C1D95' }}>₹{fmtNum(parseFloat(costPerPair) || 0)}</span>
                      </div>
                    </div>
                    <div style={{ fontSize: '11px', color: usesFallbackPrice ? '#B45309' : C.textSub, marginTop: '6px', lineHeight: 1.5 }}>
                      {usesFallbackPrice
                        ? 'Set a Dealer Amount on the category type to charge the dealer price instead of the standard amount.'
                        : 'Priced from each category type’s Dealer Amount — set it under Categories.'}
                    </div>
                  </div>

                  {/* Number of pairs */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={lbl}>Number of pairs</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <button
                        onClick={() => setNumPairs(p => Math.max(1, p - 1))}
                        style={{ width: '38px', height: '38px', borderRadius: '8px', border: `1.5px solid ${C.border}`, background: C.white, fontSize: '18px', cursor: 'pointer', color: '#6D28D9', fontWeight: 700, flexShrink: 0 }}
                      >−</button>
                      <input
                        type="number" min="1"
                        value={numPairs}
                        onChange={e => { setNumPairs(Math.max(1, parseInt(e.target.value) || 1)); setStockErr(''); }}
                        style={{ ...inp, textAlign: 'center', fontSize: '20px', fontWeight: 800, maxWidth: '100px' }}
                      />
                      <button
                        onClick={() => setNumPairs(p => p + 1)}
                        style={{ width: '38px', height: '38px', borderRadius: '8px', border: `1.5px solid #7C3AED`, background: '#F5F3FF', fontSize: '18px', cursor: 'pointer', color: '#6D28D9', fontWeight: 700, flexShrink: 0 }}
                      >+</button>
                    </div>
                  </div>

                  {/* Total preview */}
                  <div style={{
                    padding: '16px 20px', borderRadius: '12px',
                    background: orderTotal > balance ? '#FEF2F2' : 'linear-gradient(135deg,#F5F3FF,#EDE9FE)',
                    border: `1.5px solid ${orderTotal > balance ? '#FECACA' : '#DDD6FE'}`,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px',
                  }}>
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '4px' }}>
                        {fmtAmt(parseFloat(costPerPair) || 0)} × {numPairs} pairs
                      </div>
                      <div style={{ fontSize: '26px', fontWeight: 800, color: orderTotal > balance ? C.danger : '#4C1D95' }}>
                        {fmtAmt(orderTotal)}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '4px' }}>Balance After</div>
                      <div style={{ fontSize: '20px', fontWeight: 800, color: (balance - orderTotal) < 0 ? C.danger : '#059669' }}>
                        {fmtAmt(balance - orderTotal)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {stockErr && (
                <div style={{ marginTop: '12px', padding: '10px 14px', background: C.dangerBg, borderRadius: '8px', fontSize: '12px', color: C.danger, fontWeight: 600 }}>
                  ⚠ {stockErr}
                </div>
              )}
            </div>

            {/* Wizard footer */}
            <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', gap: '10px', flexShrink: 0 }}>
              <div>
                {stockStep > 1 && (
                  <button onClick={() => { setStockStep(s => s - 1); setStockErr(''); }} style={btnGhost}>
                    ← Back
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setStockModal(false)} style={btnGhost}>Cancel</button>
                {stockStep === 1 && (
                  <button onClick={goToStep2} disabled={selCats.length === 0} style={{ ...btnPurple, opacity: selCats.length === 0 ? 0.5 : 1 }}>
                    Next →
                  </button>
                )}
                {stockStep === 2 && (
                  <button
                    onClick={goToStep3}
                    disabled={selCats.some(c => !selTypes[c.id])}
                    style={{ ...btnPurple, opacity: selCats.some(c => !selTypes[c.id]) ? 0.5 : 1 }}
                  >
                    Next →
                  </button>
                )}
                {stockStep === 3 && (
                  <button
                    onClick={saveStock}
                    disabled={stockSaving || orderTotal === 0 || orderTotal > balance}
                    style={{ ...btnPurple, opacity: (stockSaving || orderTotal === 0 || orderTotal > balance) ? 0.6 : 1 }}
                  >
                    {stockSaving ? 'Saving…' : `Confirm — ${fmtAmt(orderTotal)}`}
                  </button>
                )}
              </div>
            </div>
          </div>
        </Overlay>
      )}

      {/* ── ASSIGN SERIALS MODAL ── */}
      {serialModal && (
        <Overlay>
          <div style={{
            background: C.white, borderRadius: '16px', width: '100%', maxWidth: '680px',
            maxHeight: '90vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 24px 80px rgba(0,0,0,0.3)', border: `1px solid ${C.border}`,
          }}>
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #3B0764, #1E1040)',
              padding: '20px 24px', flexShrink: 0,
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#fff' }}>🔢 Assign Serial Numbers</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', marginTop: '3px' }}>
                  {serialOrder?.order_id} · {serialOrder?.num_pairs} pair{serialOrder?.num_pairs !== 1 ? 's' : ''} per item
                </div>
              </div>
              <button onClick={() => setSerialModal(false)} style={{
                background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '50%',
                width: '28px', height: '28px', cursor: 'pointer',
                color: 'rgba(255,255,255,0.7)', fontSize: '14px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>✕</button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '22px 24px' }}>
              {serialLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: C.textSub }}>Loading inventory…</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {pairSel.map((pair, pairIdx) => {
                    const pairComplete = serialLines.every(line => pair[line.stock_item_id]);
                    return (
                      <div key={pairIdx} style={{
                        border: `2px solid ${pairComplete ? '#6EE7B7' : '#DDD6FE'}`,
                        borderRadius: '14px', overflow: 'hidden',
                        boxShadow: pairComplete ? '0 0 0 3px #ECFDF5' : 'none',
                        transition: 'all 0.2s',
                      }}>
                        {/* Pair header */}
                        <div style={{
                          padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          background: pairComplete ? 'linear-gradient(135deg,#ECFDF5,#D1FAE5)' : 'linear-gradient(135deg,#F5F3FF,#EDE9FE)',
                        }}>
                          <span style={{ fontWeight: 800, fontSize: '13px', color: pairComplete ? '#059669' : '#4C1D95' }}>
                            📦 Pair {pairIdx + 1}
                          </span>
                          {pairComplete
                            ? <span style={{ fontSize: '11px', fontWeight: 700, color: '#059669' }}>✓ Complete</span>
                            : <span style={{ fontSize: '11px', fontWeight: 600, color: '#6D28D9' }}>{serialLines.filter(l => pair[l.stock_item_id]).length}/{serialLines.length} items selected</span>
                          }
                        </div>

                        {/* One slot per category/type */}
                        <div style={{ background: C.white }}>
                          {serialLines.map((line, lineIdx) => {
                            const selId  = pair[line.stock_item_id];
                            const allInv = [
                              ...line.assigned.map(a => ({ ...a, wasAssigned: true })),
                              ...line.available,
                            ];
                            return (
                              <div key={line.stock_item_id} style={{
                                borderTop: lineIdx > 0 ? `1px solid ${C.border}` : 'none',
                                padding: '12px 16px',
                              }}>
                                {/* Slot label */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                                  <span style={{ fontSize: '14px' }}>📂</span>
                                  <span style={{ fontWeight: 700, fontSize: '12px', color: C.textMain }}>{line.category_name}</span>
                                  <span style={{ color: C.textSub }}>→</span>
                                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#6D28D9' }}>{line.type_name}</span>
                                </div>

                                {allInv.length === 0 ? (
                                  <div style={{ fontSize: '12px', color: C.danger, padding: '8px 12px', background: C.dangerBg, borderRadius: '8px' }}>
                                    No in-stock inventory available
                                  </div>
                                ) : (
                                  <SerialSearchSelect
                                    items={allInv}
                                    selectedId={selId}
                                    usedIds={pairSel
                                      .filter((_, i) => i !== pairIdx)
                                      .map(p => p[line.stock_item_id])
                                      .filter(Boolean)}
                                    onChange={invId => selectForPair(pairIdx, line.stock_item_id, invId)}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {serialErr && (
                <div style={{ marginTop: '12px', padding: '10px 14px', background: C.dangerBg, borderRadius: '8px', fontSize: '12px', color: C.danger, fontWeight: 600 }}>
                  ⚠ {serialErr}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: '10px', flexShrink: 0 }}>
              <button onClick={() => setSerialModal(false)} style={btnGhost}>Cancel</button>
              <button
                onClick={saveSerials}
                disabled={serialSaving || serialLoading}
                style={{ ...btnPurple, opacity: (serialSaving || serialLoading) ? 0.6 : 1 }}
              >
                {serialSaving ? 'Saving…' : '✓ Assign Serials'}
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ── ADD CREDIT MODAL ── */}
      {credModal && (
        <Overlay>
          <div style={{
            background: C.white, borderRadius: '16px', width: '100%', maxWidth: '440px',
            boxShadow: '0 24px 80px rgba(0,0,0,0.3)', border: `1px solid ${C.border}`, overflow: 'hidden',
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #3B0764, #1E1040)',
              padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#fff' }}>Add Credit</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', marginTop: '3px' }}>
                  Current balance: <strong style={{ color: balance >= 0 ? '#A78BFA' : '#FCA5A5' }}>{fmtAmt(balance)}</strong>
                </div>
              </div>
              <button onClick={() => setCredModal(false)} style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer', color: 'rgba(255,255,255,0.7)', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>
            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={lbl}>Amount (₹) *</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', fontWeight: 700, color: '#6D28D9' }}>₹</span>
                  <input type="number" min="1" value={credForm.amount}
                    onChange={e => { setCredForm(p => ({ ...p, amount: e.target.value })); setCredErr(''); }}
                    placeholder="0.00"
                    style={{ ...inp, paddingLeft: '32px', fontSize: '18px', fontWeight: 700, borderColor: credErr ? C.danger : C.border }}
                  />
                </div>
                {credErr && <div style={{ fontSize: '11px', color: C.danger, marginTop: '5px' }}>⚠ {credErr}</div>}
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={lbl}>Payment Mode</label>
                <select value={credForm.payment_mode} onChange={e => setCredForm(p => ({ ...p, payment_mode: e.target.value }))} style={{ ...inp, background: C.white, cursor: 'pointer' }}>
                  {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={lbl}>Reference / Transaction ID</label>
                <input value={credForm.reference} onChange={e => setCredForm(p => ({ ...p, reference: e.target.value }))} placeholder="UPI ref, cheque no., etc." style={inp} />
              </div>
              <div>
                <label style={lbl}>Description</label>
                <input value={credForm.description} onChange={e => setCredForm(p => ({ ...p, description: e.target.value }))} placeholder="e.g. Payment received for order" style={inp} />
              </div>
              {credForm.amount > 0 && (
                <div style={{ marginTop: '14px', padding: '11px 14px', borderRadius: '10px', background: '#F5F3FF', border: '1px solid #DDD6FE', fontSize: '12px', color: '#6D28D9', fontWeight: 600 }}>
                  New balance: <strong style={{ fontSize: '14px' }}>{fmtAmt(balance + parseFloat(credForm.amount || 0))}</strong>
                </div>
              )}
            </div>
            <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setCredModal(false)} style={btnGhost}>Cancel</button>
              <button onClick={saveCredit} disabled={credSaving} style={btnPurple}>{credSaving ? 'Saving…' : '+ Add Credit'}</button>
            </div>
          </div>
        </Overlay>
      )}
    </Layout>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function TransactionTable({ rows, fmtDate, fmtAmt }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
        <thead>
          <tr style={{ background: C.primaryBg, borderBottom: `2px solid ${C.border}` }}>
            {['Date & Time', 'Description', 'Mode', 'Reference', 'Amount', 'Balance'].map(h => (
              <th key={h} style={th}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((t, i) => (
            <tr key={t.id} style={{ background: i % 2 === 0 ? C.white : C.primaryBg, borderBottom: `1px solid ${C.border}` }}>
              <td style={td}><div style={{ fontSize: '12px', fontWeight: 600 }}>{fmtDate(t.created_at)}</div></td>
              <td style={td}>{t.description || <span style={{ color: C.textSub }}>—</span>}</td>
              <td style={td}>
                {t.payment_mode
                  ? <span style={{ background: '#F5F3FF', color: '#6D28D9', padding: '3px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600 }}>{t.payment_mode}</span>
                  : <span style={{ color: C.textSub }}>—</span>}
              </td>
              <td style={td}><span style={{ fontSize: '12px', fontFamily: 'monospace', color: C.textSub }}>{t.reference || '—'}</span></td>
              <td style={td}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: t.type === 'credit' ? '#059669' : C.danger }}>
                  {t.type === 'credit' ? '+' : '−'}{fmtAmt(t.amount)}
                </span>
              </td>
              <td style={td}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: t.balance_after >= 0 ? '#4C1D95' : C.danger }}>{fmtAmt(t.balance_after)}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatChip({ label, val, color, bg }) {
  return (
    <div style={{ background: bg, border: `1px solid ${color}30`, borderRadius: '10px', padding: '10px 16px' }}>
      <div style={{ fontSize: '10px', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>{label}</div>
      <div style={{ fontSize: '15px', fontWeight: 800, color }}>{val}</div>
    </div>
  );
}

function SerialSearchSelect({ items, selectedId, usedIds, onChange }) {
  const [query,    setQuery]    = useState('');
  const [expanded, setExpanded] = useState(false);

  const selected = items.find(i => i.id === selectedId);

  const filtered = items.filter(inv => {
    if (usedIds.includes(inv.id)) return false;
    const q = query.toLowerCase();
    if (!q) return true;
    return (
      (inv.item_id      || '').toLowerCase().includes(q) ||
      (inv.serial_number|| '').toLowerCase().includes(q) ||
      (inv.ref_number   || '').toLowerCase().includes(q)
    );
  });

  const select = (inv) => {
    onChange(inv.id);
    setQuery('');
    setExpanded(false);
  };

  const clear = () => {
    onChange(null);
    setQuery('');
    setExpanded(true);
  };

  return (
    <div style={{ borderRadius: '10px', border: `1.5px solid ${expanded ? '#7C3AED' : selected ? '#7C3AED' : C.border}`, overflow: 'hidden', background: C.white }}>

      {/* Selected chip row OR search input */}
      {selected && !expanded ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: '#F5F3FF' }}>
          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#7C3AED', flexShrink: 0 }} />
          <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '12px', color: '#4C1D95' }}>{selected.item_id}</span>
          {selected.serial_number && <span style={{ fontSize: '11px', color: '#6D28D9' }}>SN: {selected.serial_number}</span>}
          {selected.ref_number    && <span style={{ fontSize: '11px', color: '#6D28D9' }}>Ref: {selected.ref_number}</span>}
          <div style={{ flex: 1 }} />
          <button
            onClick={clear}
            style={{ background: '#EDE9FE', border: 'none', borderRadius: '6px', padding: '3px 8px', cursor: 'pointer', fontSize: '11px', fontWeight: 700, color: '#6D28D9' }}
          >Change</button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderBottom: expanded && filtered.length > 0 ? `1px solid ${C.border}` : 'none' }}>
          <span style={{ fontSize: '14px', flexShrink: 0 }}>🔍</span>
          <input
            autoFocus
            value={query}
            onChange={e => { setQuery(e.target.value); setExpanded(true); }}
            onFocus={() => setExpanded(true)}
            placeholder="Search serial no, ref no or INV id…"
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: '13px', color: C.textMain, background: 'transparent' }}
          />
          {query && (
            <button onClick={() => setQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textSub, fontSize: '14px' }}>×</button>
          )}
        </div>
      )}

      {/* Inline results list — no absolute positioning */}
      {expanded && (
        <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '14px', fontSize: '12px', color: C.textSub, textAlign: 'center' }}>
              {query ? 'No matches found' : items.length === usedIds.length ? 'All items used in other pairs' : 'No available inventory'}
            </div>
          ) : (
            filtered.map((inv, i) => (
              <div
                key={inv.id}
                onClick={() => select(inv)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '10px 14px', cursor: 'pointer',
                  borderTop: i > 0 ? `1px solid ${C.border}` : 'none',
                  background: C.white,
                  transition: 'background 0.12s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#F5F3FF'}
                onMouseLeave={e => e.currentTarget.style.background = C.white}
              >
                <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '12px', color: '#6D28D9', flexShrink: 0 }}>{inv.item_id}</span>
                <div style={{ flex: 1, display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {inv.serial_number && <span style={{ fontSize: '11px', color: C.textSub }}>SN: <strong style={{ color: C.textMain }}>{inv.serial_number}</strong></span>}
                  {inv.ref_number    && <span style={{ fontSize: '11px', color: C.textSub }}>Ref: <strong style={{ color: C.textMain }}>{inv.ref_number}</strong></span>}
                </div>
                <span style={{ fontSize: '11px', color: '#7C3AED', fontWeight: 600, flexShrink: 0 }}>Select →</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function Overlay({ children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(28,20,7,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '20px',
    }}>{children}</div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const th  = { textAlign: 'left', padding: '10px 14px', fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' };
const td  = { padding: '12px 14px', fontSize: '13px', color: C.textMain, verticalAlign: 'middle' };
const lbl = { display: 'block', fontSize: '12px', fontWeight: 600, color: C.textMain, marginBottom: '6px' };
const inp = { width: '100%', padding: '9px 12px', boxSizing: 'border-box', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', color: C.textMain, outline: 'none' };
const btnPurple = { padding: '10px 22px', background: 'linear-gradient(135deg,#7C3AED,#5B21B6)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 3px 10px rgba(124,58,237,0.3)', display: 'flex', alignItems: 'center', gap: '7px' };
const btnGhost  = { padding: '10px 18px', background: C.primaryBg, color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' };
